import type { ReactNode } from 'react';
import type { IconName } from '../icons';
import { Icon } from './Icon';

// Buttons (Authority §25.6 / §26):
//   primary   deep green, white text — sticky CTA 52px (01 / 03) or inline 44–48px (04)
//   secondary tonal bg.container (02 "Choose Library", 04 "Skip AI", 05 "Add Evidence")
//   tonal     bg.highest (02 "Take Photo")
//   text      transparent, primary.container text (01 "Adjust Later", 04 "Dismiss")
//   danger    error.soft ground + on.error.soft text (destructive confirmations)
//   outline   white + 1px border (04 "Review Item")
// `ghost` is kept as an alias of `text` for the Phase 1 screens.
export type ButtonVariant = 'primary' | 'secondary' | 'tonal' | 'text' | 'ghost' | 'danger' | 'outline';
export type ButtonSize = 'cta' | 'regular' | 'small';

export function Button(props: {
  children: ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  trailingIcon?: IconName;
  // Parenthetical sub text on a CTA, e.g. "(Saved to Report Draft)" (03).
  sub?: string;
  disabled?: boolean;
  submit?: boolean;
  small?: boolean;
  block?: boolean;
  testId?: string;
  ariaLabel?: string;
  className?: string;
}) {
  const variant = props.variant ?? 'primary';
  const block = props.block !== false;
  const size = props.size ?? (props.small ? 'small' : variant === 'primary' && block ? 'cta' : 'regular');
  const className = [
    'btn',
    `btn-${variant}`,
    size === 'cta' ? 'btn-cta' : '',
    size === 'small' ? 'btn-small' : '',
    block ? '' : 'btn-inline',
    props.className ?? ''
  ]
    .filter((c) => c.length > 0)
    .join(' ');
  return (
    <button
      type={props.submit ? 'submit' : 'button'}
      className={className}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testId}
      aria-label={props.ariaLabel}
    >
      {props.icon ? <Icon name={props.icon} size={20} /> : null}
      <span className="btn-label">{props.children}</span>
      {props.sub ? <span className="btn-sub">{props.sub}</span> : null}
      {props.trailingIcon ? <Icon name={props.trailingIcon} size={20} /> : null}
    </button>
  );
}

// Icon-only button with a ≥48px hit area and an accessible label (top bars, row menus, note edit).
export function IconButton(props: {
  icon: IconName;
  label: string;
  onClick?: () => void;
  tone?: 'default' | 'soft' | 'primary' | 'tonal';
  disabled?: boolean;
  testId?: string;
  iconSize?: number;
  className?: string;
}) {
  const tone = props.tone ?? 'default';
  return (
    <button
      type="button"
      className={`icon-button ${tone === 'default' ? '' : `icon-button-${tone}`} ${props.className ?? ''}`}
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props.label}
      title={props.label}
      data-testid={props.testId}
    >
      <Icon name={props.icon} size={props.iconSize} />
    </button>
  );
}
